import type { LogExport } from 'shared/services/logger/logger.types'
import { z } from 'zod'

const levelSchema = z.enum(['debug', 'info', 'warn', 'error'])
const typeSchema = z.enum([
  'error',
  'navigation',
  'action',
  'auth',
  'storage',
  'system'
])

const appSchema = z.looseObject({
  name: z.string(),
  version: z.string(),
  buildId: z.string().optional(),
  environment: z.string()
})

const snapshotSchema = z.looseObject({
  sessionId: z.string().min(1),
  startedAt: z.number(),
  endedAt: z.number().optional(),
  endReason: z.string().optional(),
  tabId: z.string().optional(),
  contextId: z.string().optional(),
  contextType: z
    .enum(['main', 'iframe', 'worker', 'service-worker'])
    .optional(),
  userAgent: z.string(),
  os: z.string(),
  browser: z.string(),
  viewport: z.object({ width: z.number(), height: z.number() }),
  screen: z.object({
    width: z.number(),
    height: z.number(),
    dpr: z.number()
  }),
  timezone: z.string(),
  locale: z.string(),
  userId: z.number().optional(),
  organizationId: z.number().optional(),
  url: z.string(),
  app: appSchema.optional()
})

const entrySchema = z.looseObject({
  schemaVersion: z.literal(2).optional(),
  id: z.string().min(1),
  timestamp: z.number(),
  type: typeSchema,
  level: levelSchema,
  event: z.string().min(1),
  payload: z.record(z.string(), z.unknown()),
  sessionId: z.string().min(1)
})

const exportSchema = z.looseObject({
  schemaVersion: z.union([z.literal(1), z.literal(2)]).optional(),
  snapshot: snapshotSchema,
  sessions: z.array(snapshotSchema).optional(),
  entries: z.array(entrySchema).max(200_000),
  health: z.unknown().optional(),
  redaction: z.unknown().optional()
})

export function parseLogExport(value: unknown): LogExport {
  return exportSchema.parse(value) as LogExport
}
